import { useCallback, useEffect } from 'react';
import { createDefaultAudioSettings, type AudioSettings, type AudioVolumeId } from '@sorrem/shared';
import { getPageAudio } from '@sorrem-client/ui/audio/page-audio';
import { installUiSounds } from '@sorrem-client/ui/audio/ui-sounds';
import { useAccountSetting } from './use-account-setting';

const DEFAULT_AUDIO_SETTINGS: Readonly<AudioSettings> = createDefaultAudioSettings();

interface AudioSettingsState {
    /** Master and channel volumes and mute; the defaults until settings load. */
    audioSettings: Readonly<AudioSettings>;
    /** Hears a volume at once, without saving it: a slider being dragged. */
    previewVolume: (id: AudioVolumeId, volume: number) => void;
    /** Saves a volume to the account (applied optimistically). */
    setVolume: (id: AudioVolumeId, volume: number) => void;
    setMuted: (muted: boolean) => void;
}

/**
 * The account's audio settings (#260), shown in the settings pop-over.
 * Account settings, so they follow the player from one browser to the next.
 */
export function useAudioSettings(): AudioSettingsState {
    const { value: audioSettings, set: setAudioSettings } = useAccountSetting('audioSettings', DEFAULT_AUDIO_SETTINGS);

    const previewVolume = useCallback((id: AudioVolumeId, volume: number): void => {
        getPageAudio().setSettings({ ...audioSettings, [id]: volume });
    }, [audioSettings]);

    const setVolume = useCallback((id: AudioVolumeId, volume: number): void => {
        setAudioSettings((current) => (current[id] === volume ? current : { ...current, [id]: volume }));
    }, [setAudioSettings]);

    const setMuted = useCallback((muted: boolean): void => {
        setAudioSettings((current) => (current.isMuted === muted ? current : { ...current, isMuted: muted }));
    }, [setAudioSettings]);

    return { audioSettings, previewVolume, setVolume, setMuted };
}

/**
 * Keeps the page's audio in step with the account's audio settings, loads
 * the sound catalog and makes the page's controls click. Mounted once, at
 * the app root, so every page sounds the same.
 */
export function useSyncAudioSettings(): void {
    const { audioSettings } = useAudioSettings();

    useEffect(() => {
        const audio = getPageAudio();
        audio.preload();
        return installUiSounds(document, audio);
    }, []);

    useEffect(() => {
        getPageAudio().setSettings(audioSettings);
    }, [audioSettings]);
}
